import React, { useEffect, useState } from 'react'
import { Box, Paper, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useTranslation } from 'react-i18next'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import ImageComponent from '@professocial/professocialFormComponents/FormComponents/ImageComponent'
import Cropper from 'react-cropper'
import MediaCropperComponent from '@professocial/professocialFormComponents/FormComponents/MediaCropperComponent'
import { Action } from 'redux'
import { MediaFileMetadata } from '@3m5/sm_publish-frontend-api'
import { useSelector } from 'react-redux'
import { getTemporaryMediaFile } from '../../../store/topic/topic.selector'
import VideoComponent from '@professocial/professocialFormComponents/FormComponents/VideoComponent'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minWidth: '300px',
    width: '100%',
  },
  imgDesc: {
    padding: theme.spacing(2),
  },
  image: {
    height: 'auto',
    maxHeight: '430px',
    width: '100%',
    objectFit: 'cover',
    borderTopLeftRadius: '5px',
    borderTopRightRadius: '5px',
  },
  actionItem: {
    marginLeft: theme.spacing(1),
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
}))

type IProps = {
  file?: MediaFileMetadata | string
  setMediaFile: (file: string | undefined, fileId?: string) => void
  getMediaData?: (params: any) => Action<any>
  allowVideoUpload?: boolean
}

const TopicMediaUpload: React.FC<IProps> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const [cropper, setCropper] = useState<any>()
  const [mediaFile, setMediaFile] = useState<string | undefined>()
  const [isVideo, setIsVideo] = useState<boolean>(false)
  const [preview, setPreview] = useState<string | undefined>()
  const [actualImage, setActualImage] = useState<string | undefined>(props.file as string)

  const temporaryMediaFile: MediaFileMetadata | undefined = useSelector(getTemporaryMediaFile)

  const getCropData = () => {
    if (typeof cropper !== 'undefined') {
      const image = cropper.getCroppedCanvas().toDataURL('image/jpeg')
      fetch(image)
        .then(res => res.blob())
        .then(blob => props.getMediaData && props.getMediaData({ file: blob }))

      setPreview(image)
    }
  }

  const onChangeImage = (img: string | undefined, file: File | undefined) => {
    setMediaFile(img)
  }

  const onChangeVideo = (video: string, file: File | undefined) => {
    setMediaFile(video)
    fetch(video)
      .then(res => res.blob())
      .then(blob => props.getMediaData && props.getMediaData({ file: blob }))

    setPreview(video)
    setIsVideo(true)
  }

  const handleCropped = (instance: Cropper) => {
    setCropper(instance)
  }

  const deleteMediaFile = () => {
    setMediaFile(undefined)
    setPreview(undefined)
    setActualImage(undefined)
    props.setMediaFile(undefined)
  }

  useEffect(() => {
    temporaryMediaFile && props.setMediaFile(temporaryMediaFile.directLink, temporaryMediaFile.mediaFileId)
    temporaryMediaFile && setPreview(temporaryMediaFile.directLink)
  }, [temporaryMediaFile])

  useEffect(() => {
    setPreview(undefined)
    if (props.file) {
      setActualImage(props.file as string)
    } else {
      setActualImage(undefined)
    }
  }, [props.file])

  return (
    <Paper className={classes.root}>
      {!mediaFile && !isVideo && (
        <Box display='flex' justifyContent='center'>
          <ImageComponent
            className={classes.image}
            src={preview || actualImage}
            fallback='/src/shared/assets/images/icon-img-placeholder.svg'
          />
        </Box>
      )}
      {!mediaFile && isVideo && (
        <Box display='flex' justifyContent='center'>
          <VideoComponent fallbackThumb='/src/shared/assets/images/icon-img-placeholder.svg' src={preview} controls />
        </Box>)}
      <Box className={classes.imgDesc}>
        <Box>
          <MediaCropperComponent
            mediaFile={mediaFile}
            hasActualImage={!!actualImage}
            getCropData={getCropData}
            handleCropped={handleCropped}
            deleteImage={deleteMediaFile}
            onChangeImage={onChangeImage}
            onChangeVideo={onChangeVideo}
            allowVideoUpload={props.allowVideoUpload}
            cropBoxResizable
          />
        </Box>
      </Box>
    </Paper>
  )
}

export default TopicMediaUpload
